import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'react-toastify';
import { ExternalLink } from 'lucide-react';

interface PartnerOffer {
  id: string;
  title: string;
  description: string;
  image_url: string;
  active: boolean;
}

const PartnerOffers: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [offers, setOffers] = useState<PartnerOffer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOffers();
  }, [currentLanguage]);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      await supabase.rpc('set_language', { lang: currentLanguage });
      
      const { data, error } = await supabase
        .from('partner_offers')
        .select('*')
        .eq('active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOffers(data || []);
    } catch (error) {
      console.error('Error fetching partner offers:', error);
      toast.error(t('errorFetchingPartnerOffers'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t('noPartnerOffers')}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {offers.map((offer) => (
        <div key={offer.id} className="border rounded-lg overflow-hidden shadow-sm">
          <div className="relative h-48 bg-gray-100">
            <img
              src={offer.image_url}
              alt={offer.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'https://placehold.co/400x200/e2e8f0/64748b?text=No+Image';
              }}
            />
          </div>
          <div className="p-5">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="text-lg font-medium text-gray-900">{offer.title}</h4>
                <p className="text-sm text-gray-500">{offer.description}</p>
              </div>
            </div>
            
            <div className="mt-4">
              <button
                onClick={() => window.open(offer.image_url, '_blank')}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                <ExternalLink className="h-5 w-5 mr-2" />
                {t('claimOffer')}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PartnerOffers;